import React, { useRef, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import Modal from '../modal';
import CopyInput from '../copy-input';
import FacebookIcon from './images/icons/facebook.svg';
import XIcon from './images/icons/x.svg';
import RedditIcon from './images/icons/reddit.svg';
import PinterestIcon from './images/icons/pinterest.svg';
import ShareIcon from './images/icons/share.svg';
import EmbedIcon from './images/icons/embed.svg';
import FacebookShareButton from 'react-share/lib/FacebookShareButton';
import TwitterShareButton from 'react-share/lib/TwitterShareButton';
import RedditShareButton from 'react-share/lib/RedditShareButton';
import PinterestShareButton from 'react-share/lib/PinterestShareButton';
import useKeyPress from '../hooks/use-key-press';
import keyConstants from '../utils/keys-constants';
import _get from 'lodash/get';

function ShareModal({
    onModalDismiss,
    onShareButtonClick,
    visible,
    target,
    content,
    dictionary,
}) {

    const embedCode = _get(content, 'embedElement');
    const shareThisVideo = _get(dictionary, 'share_video_text', 'Share this Video');
    const shareOnFacebook = _get(dictionary, 'share_with_facebook_text', "Share on Facebook");
    const shareOnX =_get(dictionary, 'share_with_x_text', "Share on X");
    const shareOnReddit = _get(dictionary, 'share_with_reddit_text', "Share on Reddit");
    const shareOnPinterest = _get(dictionary, 'share_with_pinterest_text', "Share on Pinterest");
    const copyUrl = _get(dictionary, 'share_url_text', "Copy URL");
    const copyEmbedCode = _get(dictionary, 'embed_code_text', 'Copy Embed Code');
    const dictSubset = {copied_text : _get(dictionary, 'copied_text'), copy_text :_get(dictionary, 'copy_text')};

    const firstFocusedNode = useRef(null);
    const firstModalNode = useRef(null);
    const lastModalNode = useRef(null);
    const { TAB } = keyConstants;
    // In order to keep tabbing inside the modal we must track the tab pressing
    const keysToTrack = [TAB];
    const keysTrackingCB = (event) => {

        if (event.target === lastModalNode.current) {

            event.preventDefault();
            firstModalNode.current.focus();

        }

    };

    const shiftKeyTrackingCB = (event) => {

        if (event.target === firstModalNode.current) {

            event.preventDefault();
            lastModalNode.current.focus();

        }

    };

    const callBackProps = {
        cb: keysTrackingCB,
        shiftCB: shiftKeyTrackingCB,
    };

    useKeyPress(keysToTrack, callBackProps);

    useEffect(() => {

        visible && firstFocusedNode.current.firstElementChild.focus();

    }, [visible]);

    return (
        <Modal
            target={target}
            onModalDismiss={() => onModalDismiss()}
            className="webmed-share-modal"
            visible={visible}
        >
            <header className="webmed-share-modal__header">
                <h1 className="webmed-share-modal__title">{shareThisVideo}</h1>
                <button
                    className="webmed-share-modal__close-button"
                    onClick={() => onModalDismiss()}
                    ref={firstModalNode}
                    aria-label={'Close'}
                >
                    ✕
                </button>
            </header>
            <section className="webmed-share-modal__body">
                <ul className="webmed-share-modal__social-links">
                    <li className="webmed-share-modal__social-links__link webmed-share-modal__social-links__facebook-icon"
                        ref={firstFocusedNode}
                        tabIndex="-1"
                        onClick={onShareButtonClick}
                        data-type="Facebook"
                    >
                        <FacebookShareButton
                            className="webmed-share-modal__social-links__link__button"
                            url={content.url}
                            additionalProps={{ "aria-label": shareOnFacebook }}
                        >
                            <img src={FacebookIcon} />
                        </FacebookShareButton>
                    </li>
                    <li className="webmed-share-modal__social-links__link webmed-share-modal__social-links__twitter-icon"
                        onClick={onShareButtonClick}
                        data-type="Twitter"
                    >
                        <TwitterShareButton
                            className="webmed-share-modal__social-links__link__button"
                            title={content.title}
                            url={content.url}
                            via={dictionary.twitter_handle || 'MLB'}
                            additionalProps={{ "aria-label": shareOnX }}
                        >
                            <div>
                                <img src={XIcon} />
                            </div>
                        </TwitterShareButton>
                    </li>
                    <li className="webmed-share-modal__social-links__link webmed-share-modal__social-links__reddit-icon"
                        onClick={onShareButtonClick}
                        data-type="Reddit"
                    >
                        <RedditShareButton
                            className="webmed-share-modal__social-links__link__button"
                            url={content.url}
                            title={content.title}
                            additionalProps={{ "aria-label": shareOnReddit }}
                        >
                            <img src={RedditIcon} />
                        </RedditShareButton>
                    </li>
                    <li className="webmed-share-modal__social-links__link webmed-share-modal__social-links__pinterest-icon"
                        onClick={onShareButtonClick}
                        data-type="Pinterest"
                    >
                        <PinterestShareButton
                            className="webmed-share-modal__social-links__link__button"
                            url={content.url}
                            media={content.image}
                            description={content.description}
                            additionalProps={{ "aria-label": shareOnPinterest }}
                        >
                            <img src={PinterestIcon} />
                        </PinterestShareButton>
                    </li>
                </ul>
                <div className="webmed-share-modal__links">
                    <label className="webmed-share-modal__links__label">{ copyUrl }</label>
                    <CopyInput
                        icon={ShareIcon}
                        value={content.url}
                        ariaLabel={copyUrl}
                        trackingConfig={{dataType: 'Link', handler: onShareButtonClick}}
                        dictionary={dictSubset}
                    />

                    { embedCode
                    && <Fragment>
                        <label className="webmed-share-modal__links__label">{copyEmbedCode}</label>
                        <CopyInput
                            icon={EmbedIcon}
                            value={embedCode}
                            ariaLabel={copyEmbedCode}
                            trackingConfig={{dataType: 'Embed', handler: onShareButtonClick}}
                            ref={lastModalNode}
                            dictionary={dictSubset}
                        /></Fragment> }
                </div>

            </section>
        </Modal>
    );

}

ShareModal.propTypes = {
    onModalDismiss: PropTypes.func,
    onShareButtonClick: PropTypes.func,
    visible: PropTypes.bool,
    target: PropTypes.string,
    content: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string,
        embedElement: PropTypes.string,
    }),
    dictionary: PropTypes.object,
};

ShareModal.defaultProps = {
    onModalDismiss: () => null,
    onShareButtonClick: () => null,
    visible: false,
    target: 'body',
    content: {
        url: __CLIENT__ ? window.location.href : 'https://www.mlb.com',
        title: '',
        description: '',
        image: '',
        embedElement: '',
    },
    dictionary: {},
};

export default ShareModal;
